import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { TeamRepoScore, DimensionScoreRecord, GapAnalysis } from '@sdd-maturity/shared-types';
import { RepoCard } from '../components/RepoCard';
import { Top10List, type Top10Row } from '../components/Top10List';
import { FrameworkBadge } from '../components/FrameworkBadge';
import { BucketBadge } from '../components/BucketBadge';
import { DeltaIndicator } from '../components/DeltaIndicator';
import { TeamScoreHeader } from '../components/TeamScoreHeader';
import { RepoMaturityDistribution } from '../components/RepoMaturityDistribution';
import { HighlightCard } from '../components/HighlightCard';
import type { Highlight } from '../lib/rollup';
import { PhaseCard } from './Overview';
import { StrengthsPanel } from '../components/StrengthsPanel';
import { ScoreDistribution } from '../components/ScoreDistribution';
import { DimensionHeatmap } from '../components/DimensionHeatmap';
import { ScoreHistoryChart } from '../components/ScoreHistoryChart';
import type { ScoreHistory, DimensionName, MaturityLevel, DiscoveryFeatureListView, SledTeam } from '@sdd-maturity/shared-types';
import { RepoTable } from '../components/RepoTable';
import { FeatureTable } from '../components/FeatureTable';
import { TeamLeaderboard } from '../components/TeamLeaderboard';
import type { LeaderboardRow } from '../lib/rollup';
import { StandingsTable } from './Overview';
import type { StandingsRow } from '../lib/standings';
import { Button, type ButtonVariant, type ButtonSize } from '../components/Button';
import { PlusIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

/**
 * Dev-only component gallery used to verify Figma capture against the live app
 * (FR-3 / STEP-3). Rendered OUTSIDE Layout (see App.tsx) so it isn't forced into
 * dark mode — the toggle below sets `data-theme` on its own wrapper, letting both
 * light (`:root`) and dark (`[data-theme='dark']`) be screenshotted. Seeded with
 * the pilot components now; structured as an extensible `sections` array so the
 * STEP-6 scale-out can append the remaining components here. Not linked in nav.
 */

function makeRepo(overrides: Partial<TeamRepoScore>): TeamRepoScore {
  return {
    orgName: 'cox-automotive',
    repoName: 'maturity-service',
    overallScore: 3.7,
    overallLevel: 'L3',
    framework: 'React',
    dimensions: [],
    lastScanTimestamp: new Date().toISOString(),
    findingsCount: { open: 4, resolved: 12 },
    findingsDigest: [],
    componentName: 'Platform',
    lastActivityDate: new Date(Date.now() - 3 * 86_400_000).toISOString(),
    commitCount60d: 8,
    ...overrides,
  };
}

const repoInteractive = makeRepo({ repoName: 'maturity-service', overallLevel: 'L3' });
const repoTop = makeRepo({
  repoName: 'scoring-engine',
  overallLevel: 'L5',
  overallScore: 4.6,
  framework: 'Vue',
  findingsCount: { open: 1, resolved: 30 },
});
const repoSkipped = makeRepo({
  repoName: 'legacy-scripts',
  overallLevel: 'SKIPPED' as TeamRepoScore['overallLevel'],
  framework: 'stale',
});
const repoEvalFailed = makeRepo({
  repoName: 'broken-pipeline',
  overallScore: -1,
  framework: 'evaluation-failed',
});

const top10Rows: Top10Row[] = [
  { key: 'r1', teamName: 'Platform', itemName: 'scoring-engine', score: 95, href: '#', badge: <span className="badge badge-l5">L5</span> },
  { key: 'r2', teamName: 'Insights', itemName: 'maturity-service', score: 78, href: '#', badge: <span className="badge badge-l4">L4</span> },
  { key: 'r3', teamName: 'Ingest', itemName: 'sled-sync', score: 64, href: '#', badge: <span className="badge badge-l3">L3</span> },
  { key: 'r4', teamName: 'Tooling', itemName: 'cli-runner', score: 45, badge: <span className="badge badge-l2">L2</span> },
  { key: 'r5', teamName: 'Sandbox', itemName: 'spike-prototype', score: 22, badge: <span className="badge badge-l1">L1</span> },
];

function BadgeRow() {
  return (
    <div className="flex items-center gap-sm" style={{ flexWrap: 'wrap' }}>
      <span className="badge badge-l1">L1 - Initial</span>
      <span className="badge badge-l2">L2 - Developing</span>
      <span className="badge badge-l3">L3 - Defined</span>
      <span className="badge badge-l4">L4 - Managed</span>
      <span className="badge badge-l5">L5 - Optimizing</span>
      <span className="badge badge-info">info</span>
      <span className="badge badge-warning">warning</span>
      <FrameworkBadge framework="React" />
      <FrameworkBadge framework="maintenance-only" />
      <FrameworkBadge framework="infra-only" />
      <FrameworkBadge framework="none" />
    </div>
  );
}

function RepoCardSamples() {
  return (
    <div className="flex" style={{ flexDirection: 'column', gap: 'var(--genai-space-md)' }}>
      <RepoCard repo={repoInteractive} teamNumber="100123" showComponent />
      <RepoCard repo={repoTop} teamNumber="100123" showComponent />
      <RepoCard repo={repoInteractive} />
      <RepoCard repo={repoSkipped} teamNumber="100123" showComponent />
      <RepoCard repo={repoEvalFailed} teamNumber="100123" />
    </div>
  );
}

function Top10Samples() {
  return (
    <div className="flex" style={{ gap: 'var(--genai-space-lg)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 360px' }}>
        <Top10List title="Top Repositories" itemHeader="Repository" rows={top10Rows} />
      </div>
      <div style={{ flex: '1 1 360px' }}>
        <Top10List title="Top Repositories (empty)" itemHeader="Repository" rows={[]} />
      </div>
    </div>
  );
}

function ButtonSamples() {
  const variants: ButtonVariant[] = ['primary', 'secondary', 'tertiary'];
  const sizes: ButtonSize[] = ['L', 'M', 'S', 'XS'];
  return (
    <div className="flex" style={{ flexDirection: 'column', gap: 'var(--genai-space-lg)' }}>
      {/* Variant × size matrix */}
      {variants.map((variant) => (
        <div key={variant} className="flex items-center gap-md" style={{ flexWrap: 'wrap' }}>
          {sizes.map((size) => (
            <Button key={size} variant={variant} size={size}>
              {variant} {size}
            </Button>
          ))}
          <Button variant={variant} disabled>
            disabled
          </Button>
        </div>
      ))}
      {/* Icon slots — use leftIcon OR rightIcon, not both */}
      <div className="flex items-center gap-md" style={{ flexWrap: 'wrap' }}>
        <Button variant="primary" leftIcon={<PlusIcon />}>
          New scan
        </Button>
        <Button variant="secondary" rightIcon={<ArrowRightIcon />}>
          Continue
        </Button>
        <Button variant="tertiary" size="S" leftIcon={<PlusIcon />}>
          Add
        </Button>
      </div>
    </div>
  );
}

function AtomSamples() {
  return (
    <div className="flex items-center gap-sm" style={{ flexWrap: 'wrap' }}>
      <BucketBadge bucket="sdd-discovery" />
      <BucketBadge bucket="partial-sdd" />
      <BucketBadge bucket="not-sdd" />
      <DeltaIndicator current={4.2} previous={3.7} />
      <DeltaIndicator current={3.0} previous={3.8} />
      <DeltaIndicator current={3.5} previous={3.5} />
      <DeltaIndicator current={3.5} />
    </div>
  );
}

// Batch 2 — summary / stat cards. Varied levels so the maturity distribution bar
// renders all five segments.
const headerRepos: TeamRepoScore[] = [
  makeRepo({ repoName: 'scoring-engine', overallLevel: 'L5', overallScore: 4.6, framework: 'Vue', recommendationsCount: 2 }),
  makeRepo({ repoName: 'maturity-service', overallLevel: 'L4', overallScore: 3.9, framework: 'React', recommendationsCount: 5 }),
  makeRepo({ repoName: 'sled-sync', overallLevel: 'L3', overallScore: 3.1, framework: 'Node', recommendationsCount: 3 }),
  makeRepo({ repoName: 'cli-runner', overallLevel: 'L2', overallScore: 2.2, framework: 'Go', recommendationsCount: 1 }),
  makeRepo({ repoName: 'spike-prototype', overallLevel: 'L1', overallScore: 1.4, framework: 'React', recommendationsCount: 0 }),
];

const highlightSamples: Highlight[] = [
  { kind: 'top', label: 'Top Performer', itemName: 'scoring-engine', teamName: 'Platform', score: 95, detail: 'highest score', href: '#' },
  { kind: 'improved', label: 'Most Improved', itemName: 'maturity-service', teamName: 'Insights', score: 78, detail: '+12 this scan', href: '#' },
  { kind: 'adopted', label: 'Newly Adopted', itemName: 'sled-sync', teamName: 'Ingest', score: 64, detail: 'first SDD scan' },
];

function PhaseCardSamples() {
  return (
    <div className="grid grid-cols-2 gap-md">
      <PhaseCard phase="discovery" score={3.8} node={{ tier: 4, number: '100123' }} />
      <PhaseCard phase="delivery" score={null} node={{ tier: 4, number: '100123' }} />
    </div>
  );
}

function HighlightSamples() {
  return (
    <div className="grid grid-cols-3 gap-md">
      {highlightSamples.map((h) => <HighlightCard key={h.kind} highlight={h} />)}
    </div>
  );
}

// Batch 4 — panels.
const strengthsRepos: TeamRepoScore[] = [
  makeRepo({ repoName: 'scoring-engine', dimensions: [
    { dimension: 'constitution-maturity', level: 'L4', score: 78, confidence: 0.9, strengths: [{ description: 'Clear constitution with enforced principles', evidence: '.specify/' }] },
    { dimension: 'artifact-traceability', level: 'L4', score: 80, confidence: 0.9, strengths: [{ description: 'Specs link to tasks and commits', evidence: 'plans/' }] },
  ] }),
  makeRepo({ repoName: 'maturity-service', dimensions: [
    { dimension: 'constitution-maturity', level: 'L3', score: 70, confidence: 0.8, strengths: [{ description: 'Clear constitution with enforced principles', evidence: '.specify/' }] },
    { dimension: 'feedback-loop', level: 'L3', score: 65, confidence: 0.8, strengths: [{ description: 'CI gates block merges on coverage drop', evidence: 'ci.yml' }] },
  ] }),
];

const distDimensions: DimensionScoreRecord[] = [
  { dimension: 'constitution-maturity', level: 'L4', score: 82, confidence: 0.9 },
  { dimension: 'artifact-traceability', level: 'L3', score: 64, confidence: 0.8 },
  { dimension: 'supplemental-context', level: 'L2', score: 48, confidence: 0.7 },
  { dimension: 'feedback-loop', level: 'L4', score: 76, confidence: 0.85 },
  { dimension: 'ai-collaboration', level: 'L3', score: 58, confidence: 0.75 },
  { dimension: 'framework-detection', level: 'L5', score: 93, confidence: 0.95 },
];

// Batch 5 — ScoreHistoryChart (Recharts; captured as a STATIC reference only, AC-6.3).
const SHC_DIMS: DimensionName[] = [
  'constitution-maturity', 'artifact-traceability', 'supplemental-context',
  'feedback-loop', 'ai-collaboration', 'framework-detection',
];
function histPoint(ts: string, overall: number, level: MaturityLevel): ScoreHistory {
  return {
    scanTimestamp: ts,
    overallScore: overall,
    overallLevel: level,
    dimensions: SHC_DIMS.map((d, i) => ({
      dimension: d, level, score: Math.max(0, Math.min(100, overall - 12 + i * 5)), confidence: 0.85,
    })),
  };
}
// Batch 3 — tables.
const tableRepos: TeamRepoScore[] = [
  repoTop, repoInteractive, repoSkipped, repoEvalFailed,
];

const featureSamples = [
  { teamNumber: '100123', formattedId: 'F1024', featureName: 'Spec-driven checkout flow', overallScore: 88, overallLevel: 'L5', classificationBucket: 'sdd-discovery', scanTimestamp: new Date(Date.now() - 2 * 86_400_000).toISOString(), lowConfidence: false, dimensions: [{ recommendation: 'Add BDD acceptance criteria' }, { recommendation: 'Link to constitution' }] },
  { teamNumber: '100123', formattedId: 'F1025', featureName: 'Inventory sync worker', overallScore: 61, overallLevel: 'L3', classificationBucket: 'partial-sdd', scanTimestamp: new Date(Date.now() - 5 * 86_400_000).toISOString(), lowConfidence: false, dimensions: [{ recommendation: 'Document scope' }] },
  { teamNumber: '100123', formattedId: 'F1026', featureName: 'Legacy report export', overallScore: 24, overallLevel: 'L1', classificationBucket: 'not-sdd', scanTimestamp: new Date(Date.now() - 9 * 86_400_000).toISOString(), lowConfidence: true, dimensions: [] },
] as unknown as DiscoveryFeatureListView[];

const leaderboardRows: LeaderboardRow[] = [
  { unit: { number: '200', name: 'Checkout Stream', tier: 3, teamCount: 4 }, avg: 82, count: 12, levelCounts: { L5: 3, L4: 5, L3: 3, L2: 1 } },
  { unit: { number: '201', name: 'Inventory Stream', tier: 3, teamCount: 3 }, avg: 67, count: 9, levelCounts: { L4: 2, L3: 4, L2: 3 } },
  { unit: { number: '202', name: 'Platform Stream', tier: 3, teamCount: 2 }, avg: 54, count: 6, levelCounts: { L3: 2, L2: 3, L1: 1 } },
];

function sledTeam(number: string, name: string): SledTeam {
  return { number, name, tier: 4, tierLabel: 'Team', level: 'L3', active: true };
}
const standingsRows: StandingsRow[] = [
  { node: sledTeam('100123', 'Platform Core'), discoveryScore: 84, deliveryScore: 78, teamCount: 1 },
  { node: sledTeam('100124', 'Insights'), discoveryScore: 62, deliveryScore: null, teamCount: 1 },
  { node: sledTeam('100125', 'Ingest'), discoveryScore: null, deliveryScore: 49, teamCount: 3 },
];

const scoreHistory: ScoreHistory[] = [
  histPoint('2026-01-15T10:00:00Z', 42, 'L2'),
  histPoint('2026-02-15T10:00:00Z', 51, 'L3'),
  histPoint('2026-03-15T10:00:00Z', 58, 'L3'),
  histPoint('2026-04-15T10:00:00Z', 67, 'L4'),
  histPoint('2026-05-15T10:00:00Z', 74, 'L4'),
  histPoint('2026-06-15T10:00:00Z', 83, 'L4'),
];

const heatmapGaps: GapAnalysis[] = [
  { dimension: 'constitution-maturity', gapType: 'missing-principles', frequency: 80, affectedRepos: ['scoring-engine'], averageSeverity: 'critical', suggestedPriority: 1 },
  { dimension: 'artifact-traceability', gapType: 'no-spec-links', frequency: 60, affectedRepos: ['maturity-service'], averageSeverity: 'high', suggestedPriority: 2 },
  { dimension: 'supplemental-context', gapType: 'sparse-context', frequency: 40, affectedRepos: ['cli-runner'], averageSeverity: 'medium', suggestedPriority: 3 },
  { dimension: 'feedback-loop', gapType: 'manual-feedback', frequency: 30, affectedRepos: ['sled-sync'], averageSeverity: 'low', suggestedPriority: 4 },
];

const sections: { title: string; node: ReactNode }[] = [
  { title: 'Badge', node: <BadgeRow /> },
  { title: 'Button (Interstate)', node: <ButtonSamples /> },
  { title: 'Atoms', node: <AtomSamples /> },
  { title: 'RepoCard', node: <RepoCardSamples /> },
  { title: 'Top10List', node: <Top10Samples /> },
  { title: 'TeamScoreHeader', node: <TeamScoreHeader repos={headerRepos} /> },
  { title: 'RepoMaturityDistribution', node: <RepoMaturityDistribution repos={headerRepos} /> },
  { title: 'PhaseCard', node: <PhaseCardSamples /> },
  { title: 'HighlightCard', node: <HighlightSamples /> },
  { title: 'StrengthsPanel', node: <StrengthsPanel mode="delivery" repos={strengthsRepos} /> },
  { title: 'ScoreDistribution', node: <div className="card"><ScoreDistribution dimensions={distDimensions} /></div> },
  { title: 'DimensionHeatmap', node: <DimensionHeatmap gaps={heatmapGaps} /> },
  { title: 'ScoreHistoryChart (static reference — Recharts viz, AC-6.3)', node: <div className="card"><ScoreHistoryChart data={scoreHistory} showDimensions /></div> },
  { title: 'RepoTable', node: <RepoTable repos={tableRepos} teamNumber="100123" showComponent /> },
  { title: 'FeatureTable', node: <FeatureTable features={featureSamples} /> },
  { title: 'TeamLeaderboard', node: <TeamLeaderboard rows={leaderboardRows} title="Stream leaderboard" unitColLabel="Stream" unitLabel="Teams" buildHref={() => '#'} /> },
  { title: 'StandingsTable', node: <StandingsTable rows={standingsRows} onDrill={() => {}} childLabels={{ singular: 'Team', plural: 'Teams' }} /> },
];

export function PreviewGallery() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  // The app hard-codes `data-theme="dark"` on <html> (index.html), and the dark
  // CSS custom properties inherit from there — so a nested wrapper can't reset to
  // light. Drive the document root attribute directly and restore it on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.getAttribute('data-theme');
    if (theme === 'dark') html.setAttribute('data-theme', 'dark');
    else html.removeAttribute('data-theme');
    return () => {
      if (prev) html.setAttribute('data-theme', prev);
      else html.removeAttribute('data-theme');
    };
  }, [theme]);

  return (
    <div
      data-testid="preview-gallery"
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--genai-sem-page-bg)',
        color: 'var(--genai-sem-text-primary)',
        padding: 'var(--genai-space-lg)',
        fontFamily: 'var(--genai-font-body)',
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 'var(--genai-space-lg)' }}>
        <h1 className="text-lg font-bold">Component Preview Gallery</h1>
        <div className="flex items-center gap-sm">
          <span className="text-sm text-muted">Theme:</span>
          <button
            className="badge badge-info"
            data-testid="preview-theme-toggle"
            onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            style={{ cursor: 'pointer', border: 'none' }}
          >
            {theme === 'light' ? 'Light → switch to Dark' : 'Dark → switch to Light'}
          </button>
        </div>
      </div>

      {sections.map((s) => (
        <section key={s.title} data-testid={`preview-section-${s.title}`} style={{ marginBottom: 'var(--genai-space-2xl)' }}>
          <h2 className="text-sm font-semibold text-secondary" style={{ marginBottom: 'var(--genai-space-md)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {s.title}
          </h2>
          {s.node}
        </section>
      ))}
    </div>
  );
}
